import React, { useState, useEffect, useRef } from "react";
import { CiSearch } from "react-icons/ci";
import { MdKeyboardArrowRight } from "react-icons/md";
import { IoClose } from "react-icons/io5";
import { Select } from "antd";
import axios from "axios";
import { localurl } from "../components/utils";
import { FaRegUser } from "react-icons/fa6";
import { LuBot } from "react-icons/lu";
import BasicDateRange from "../components/BasicDateRange";

const ChatHistory = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [chatData, setChatData] = useState(null);
  const [chatDetails, setChatDetails] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [showDatePicker, setShowDatePicker] = useState(true);
  const datePickerRef = useRef(null);

  const handleDateChange = async (newRange) => {
    if (!newRange[0] || !newRange[1]) return;
    const start_date = newRange[0].toISOString().split("T")[0];
    const end_date = newRange[1].toISOString().split("T")[0];

    try {
      const response = await axios.get(`${localurl}/chat-history`, {
        params: { start_date, end_date },
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setChatData(response.data);

    } catch (error) {
      console.error("Error fetching filtered data:", error);
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
    }
    if (showDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDatePicker]);

  const limit = 10;
  const fetchData = async (page = 0, query = "", status = "all") => {
    try {
      const offset = page;
      const response = await axios.get(`${localurl}/chat-history`, {
        params: {
          limit,
          offset,
          search_by_ip: query,
          status: status !== "all" ? status : undefined,
        },
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setChatData(response.data);
      setTotalPages(Math.ceil(response.data.total_count / limit));
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching chat data:", error);
    }
  };

  useEffect(() => {
    fetchData(currentPage, searchQuery, selectedStatus);
  }, [currentPage, searchQuery, selectedStatus]);

  // Scroll to top whenever the page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const handleOpenPopup = async (msg) => {
    setIsOpen(true);
    try {
      const response = await fetch(`${localurl}/chat-details/${msg.id}`, {
        method: "GET",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch chat details");
      }

      const data = await response.json();
      setChatDetails(data);
    } catch (err) {
      console.log(err.message);
    }
  };

  const handleClosePopup = () => {
    setIsOpen(false);
  };

  const handleSearch = () => {
    setCurrentPage(0);
    setSearchQuery(searchInput);
  };

  return (
    <div className="w-full">
      <h1 className="text-black font-bold text-2xl mb-3">Chat History</h1>
      <p className="font-normal text-base text-gray-600 mb-10">
        View and search through all your AI chatbot conversations.
      </p>

      <div className="flex flex-row justify-between">
        <div className="flex flex-row relative mt-3 h-10 gap-2 md:visible invisible">
          <CiSearch
            className="absolute left-3 size-5 top-2.5"
            style={{ color: "#A3A3A3" }}
          />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="md:w-96 rounded-md border-2 border-gray-200 pl-10 text-sm font-normal"
            type="text"
            placeholder="Search for conversation..."
            style={{ color: "#000000" }}
          />
        </div>
        <div className="flex flex-row gap-2 items-center">
          <BasicDateRange onDateChange={handleDateChange} />
          <Select
            className="w-32 h-10"
            value={selectedStatus}
            onChange={(value) => {
              setCurrentPage(0);
              setSelectedStatus(value);
            }}
            options={[
              { value: "all", label: "All Status" },
              { value: "escalated", label: "Escalated" },
              { value: "completed", label: "Completed" },
            ]}
          />
        </div>
      </div>

      {!chatData?.data ? (
        <div className="flex justify-center items-center py-10">
          <p className="text-gray-500 text-lg">Loading conversations...</p>
        </div>
      ) : (
        chatData.data.map((msg) => (
          <div
            key={msg.id}
            className="border-2 mt-5 border-gray-200 rounded-lg p-5 w-full"
          >
            <div className="flex justify-between">
              <div className="flex flex-col">
                <div className="flex gap-3 items-center">
                  <p className="text-sm font-semibold text-black">{msg.ip}</p>
                  <p
                    className={`text-xs font-medium px-3 py-1 rounded-xl border-2 border-gray-100 ${msg.status === "completed" ? "bg-greenbg text-green" : ""}
                    ${msg.status === "escalated" ? "bg-[#FFFBEB] text-[#B45309]" : ""}`}
                  >
                    {msg.status}
                  </p>
                </div>
                <div className="flex gap-2 mt-2">
                  <p className="text-xs text-gray-500">{msg.country}</p>
                  <p className="text-xs text-gray-500">({msg.region})</p>
                </div>
              </div>
              <div className="flex flex-col text-right">
                <p className="text-xs text-gray-500">{msg.date}</p>
                <p className="text-xs text-gray-500">{msg.time}</p>
              </div>
            </div>
            <p className="text-sm text-black mt-5">{msg.last_query}</p>
            <div className="flex justify-between mt-7">
              <div className="flex gap-3 text-xs text-gray-500">
                <p>{msg.time_spent}</p>
                <p>{"Total Queries: " + msg.total_queries}</p>
              </div>
              <button
                className="text-green text-sm font-semibold flex items-center"
                onClick={() => handleOpenPopup(msg)}
              >
                View Conversation <MdKeyboardArrowRight className="text-xl" />
              </button>
            </div>
          </div>
        ))
      )}

      {isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
          <div
            className="bg-white p-6 rounded-lg shadow-md relative w-3/4 md:w-1/2 overflow-auto"
            style={{ maxHeight: "500px" }}
          >
            <button
              onClick={handleClosePopup}
              className="text-black text-2xl p-1 rounded sticky top-0 left-full"
            >
              <IoClose />
            </button>
            {chatDetails ? (
              <div>
                <h4 className="text-base font-semibold mb-3">
                  Conversation with{" "}
                  {chatDetails.customer_ip}
                </h4>
                {chatDetails.data && chatDetails.data.length > 0 ? (
                  chatDetails.data.map((day, dayIndex) => (
                    <div key={dayIndex} className="flex flex-col items-start">
                      <div className="text-center items-center justify-center mx-auto text-sm text-gray-600 font-medium my-4 bg-greenbg px-4 py-2 rounded-xl ">
                        {day.date}
                      </div>
                      {day.chats.map((chat, chatIndex) => (
                        <div key={chatIndex} className="-mt-2 flex flex-col w-full">
                          {chat.sender === "Client" ? (
                            <div className="flex flex-row-reverse justify-end w-full gap-3 mt-2">
                              <div className="shadow-lg w-8 h-8 rounded-md flex justify-center items-center mt-2 text-black">
                                <FaRegUser />
                              </div>
                              <div className="flex flex-col items-end w-full gap-3 mt-3">
                                <div className="flex flex-row-reverse gap-2 items-center">
                                  <p className="text-sm font-medium text-black">{chat.sender} </p>
                                  <div className="ml-1 text-xs text-gray-400">
                                    {new Date(chat.created_at).toLocaleTimeString(
                                      [],
                                      {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: true,
                                      }
                                    )}
                                  </div>
                                </div>
                                <p className="text-sm font-medium text-black rounded-md p-2 bg-gray-100">
                                  {chat.message}
                                </p>
                              </div>
                            </div>
                          ) : (
                            <div className="flex gap-3 mt-2">
                              <div className="shadow-lg min-w-8 w-8 h-8 rounded-md flex justify-center items-center mt-2 text-black">
                                {chat.sender === "AI Assistant" ? <LuBot className="text-xl" /> : <FaRegUser />}
                              </div>
                              <div className="flex flex-col gap-3 mt-3">
                                <p className="text-sm font-medium text-black">
                                  {chat.sender}
                                  <span className="ml-1 text-xs text-gray-400">
                                    {new Date(chat.created_at).toLocaleTimeString(
                                      [],
                                      {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: true,
                                      }
                                    )}
                                  </span>
                                </p>
                                <p className="text-sm font-medium text-black rounded-md p-2 bg-gray-100">
                                  {chat.message}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ))
                ) : (
                  <p>No Conversation Found</p>
                )}
              </div>
            ) : (
              <p>Loading chat details...</p>
            )}
          </div>
        </div>
      )}

      {chatData?.data && totalPages > 1 && (
        <div className="flex justify-center mt-10">
          <div className="flex gap-2 items-center">
            {/* Previous Button */}
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
              disabled={currentPage === 0}
              className={`px-3 py-1 rounded-md ${currentPage === 0
                ? "bg-gray-200 text-gray-500"
                : "bg-green text-white"
                }`}
            >
              Previous
            </button>

            {/* Page Numbers */}
            {Array.from({ length: totalPages }, (_, index) => index)
              .filter((pageNum) => {
                return (
                  pageNum === 0 || // always show first page
                  pageNum === totalPages - 1 || // always show last page
                  Math.abs(currentPage - pageNum) <= 1 // show current, prev and next
                );
              })
              .reduce((acc, pageNum, i, arr) => {
                // Add ellipsis if there's a gap
                if (i > 0 && pageNum - arr[i - 1] > 1) {
                  acc.push('ellipsis');
                }
                acc.push(pageNum);
                return acc;
              }, [])
              .map((pageNum, i) =>
                pageNum === 'ellipsis' ? (
                  <span key={`ellipsis-${i}`} className="px-2">...</span>
                ) : (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 rounded-md ${currentPage === pageNum
                      ? "bg-green text-white"
                      : "bg-gray-100 text-black"
                      }`}
                  >
                    {pageNum + 1}
                  </button>
                )
              )}

            {/* Next Button */}
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))}
              disabled={currentPage === totalPages - 1}
              className={`px-3 py-1 rounded-md ${currentPage === totalPages - 1
                ? "bg-gray-200 text-gray-500"
                : "bg-green text-white"
                }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default ChatHistory;
